import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { DataManagerService } from "./data-manager.service";
import {
  HudThemeStatistic,
  PlayerHudStatistic,
  normalizeHudThemeStatistics,
} from "../../shared/models/table-hud-statistic";

export interface PlayerHudStatsResponse {
  playerId: number;
  stats: HudThemeStatistic[];
}

export interface TableHudStatUpdate {
  playerId: number;
  stat: PlayerHudStatistic;
}

@Injectable({ providedIn: "root" })
export class PlayerHudStatsService {
  private readonly statsReceived$ = new Subject<PlayerHudStatsResponse>();
  private readonly tableHudStatUpdated$ = new Subject<TableHudStatUpdate>();
  private pendingPlayerId: number | null = null;

  readonly playerHudStatsUpdated$ = this.statsReceived$.asObservable();
  readonly tableHudStatsUpdated$ = this.tableHudStatUpdated$.asObservable();

  getPendingPlayerId(): number | null {
    return this.pendingPlayerId;
  }

  constructor(private dataManager: DataManagerService) {}

  notifyTableHudStatUpdated(playerId: number, stat: PlayerHudStatistic) {
    this.tableHudStatUpdated$.next({ playerId, stat });
  }

  requestPlayerHudStats(playerId: number) {
    this.pendingPlayerId = playerId;
    this.dataManager.getPlayerHudStats(playerId);
  }

  handlePlayerHudStatisticResponse(
    playerId: number,
    rawStats: any[] | undefined,
  ) {
    const stats = normalizeHudThemeStatistics(rawStats);
    this.statsReceived$.next({ playerId, stats });
    this.pendingPlayerId = null;
  }

  waitForPlayerHudStats(playerId: number): Observable<HudThemeStatistic[]> {
    return new Observable((observer) => {
      const sub = this.statsReceived$
        .pipe(
          filter((response) => response.playerId === playerId),
          take(1),
          map((response) => response.stats),
        )
        .subscribe({
          next: (stats) => {
            observer.next(stats);
            observer.complete();
          },
          error: (error) => observer.error(error),
        });

      this.requestPlayerHudStats(playerId);

      return () => sub.unsubscribe();
    });
  }

  pickThemeForTable(
    stats: HudThemeStatistic[],
    hudThemeId?: number | null,
  ): HudThemeStatistic | null {
    if (!stats?.length) {
      return null;
    }

    if (hudThemeId != null) {
      const themed = stats.find((stat) => stat.hudThemeId === hudThemeId);
      if (themed) {
        return themed;
      }
    }

    return stats[0];
  }
}
